import { useState, useCallback, useRef } from 'react';
import { rechargeApi } from '@/services/api';
import {
  GenerateLinkRequest,
  GenerateLinkResponse,
  GenerateLinkData,
  ApiError,
  HookState
} from '@/services/types';

// Hook 配置选项
interface UseGenerateLinkOptions {
  // 成功回调
  onSuccess?: (data: GenerateLinkData) => void;
  // 错误回调
  onError?: (error: ApiError) => void;
  // 自动重试次数
  retryCount?: number;
  // 重试延迟（毫秒）
  retryDelay?: number;
  // 请求超时时间（毫秒）
  timeout?: number;
}

// Hook 返回值
interface UseGenerateLinkReturn {
  // 当前状态
  data: GenerateLinkData | null;
  loading: boolean;
  error: ApiError | null;
  
  // 生成支付链接
  generateLink: (params: GenerateLinkRequest) => Promise<GenerateLinkData | null>;
  
  // 重置状态
  reset: () => void;
  
  // 最后一次请求的参数
  lastParams: GenerateLinkRequest | null;
  
  // 重试最后一次请求
  retry: () => Promise<GenerateLinkData | null>;
}

// 默认配置
const DEFAULT_OPTIONS: Required<UseGenerateLinkOptions> = {
  onSuccess: () => {},
  onError: () => {},
  retryCount: 3,
  retryDelay: 1000,
  timeout: 15000 // 15秒超时
};

export function useGenerateLink(options: UseGenerateLinkOptions = {}): UseGenerateLinkReturn {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  // 状态管理
  const [state, setState] = useState<HookState<GenerateLinkData>>({
    data: null,
    loading: false,
    error: null
  });
  
  const [lastParams, setLastParams] = useState<GenerateLinkRequest | null>(null);
  
  // 引用管理
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const retryTimerRef = useRef<number | null>(null);

  // 清理函数
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // 安全的状态更新
  const safeSetState = useCallback((newState: Partial<HookState<GenerateLinkData>>) => {
    if (mountedRef.current) {
      setState((prevState: HookState<GenerateLinkData>) => ({ ...prevState, ...newState }));
    }
  }, []);

  // 验证请求参数
  const validateParams = useCallback((params: GenerateLinkRequest): string | null => {
    if (!params.channel) {
      return '支付渠道不能为空';
    }
    
    if (!params.type) {
      return '支付类型不能为空';
    }
    
    if (!params.recharge_id) {
      return '充值产品ID不能为空';
    }

    return null;
  }, []);

  // 生成支付链接的核心函数
  const executeGenerateLink = useCallback(async (
    params: GenerateLinkRequest,
    currentRetry = 0
  ): Promise<GenerateLinkData | null> => {
    // 验证参数
    const validationError = validateParams(params);
    if (validationError) {
      const error: ApiError = {
        error_code: -1,
        message: validationError,
        details: { params }
      };
      
      safeSetState({ error, loading: false });
      opts.onError(error);
      return null;
    }

    // 如果正在请求中，取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();

    try {
      // 设置加载状态
      safeSetState({ loading: true, error: null });

      // 记录请求参数
      setLastParams(params);

      // 发起请求
      const response: GenerateLinkResponse = await rechargeApi.generatePaymentLink(params);

      // 检查组件是否还挂载
      if (!mountedRef.current) return null;

      // 更新状态
      safeSetState({
        data: response.data,
        loading: false,
        error: null
      });

      // 调用成功回调
      opts.onSuccess(response.data);

      return response.data;

    } catch (error) {
      if (!mountedRef.current) return null;

      // 处理取消请求的情况
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }

      const apiError: ApiError = {
        error_code: error instanceof Error ? -1 : (error as any).errorCode || -1,
        message: error instanceof Error ? error.message : '生成支付链接失败',
        details: { error, params }
      };

      // 重试逻辑
      if (currentRetry < opts.retryCount) {
        console.warn(`生成支付链接失败，${opts.retryDelay}ms 后进行第 ${currentRetry + 1} 次重试:`, error);
        
        return new Promise((resolve) => {
          retryTimerRef.current = window.setTimeout(async () => {
            if (mountedRef.current) {
              const result = await executeGenerateLink(params, currentRetry + 1);
              resolve(result);
            } else {
              resolve(null);
            }
          }, opts.retryDelay * Math.pow(2, currentRetry)); // 指数退避
        });
      }

      // 重试次数用完，更新错误状态
      safeSetState({
        loading: false,
        error: apiError
      });

      // 调用错误回调
      opts.onError(apiError);

      console.error('生成支付链接失败:', error);
      return null;

    } finally {
      abortControllerRef.current = null;
    }
  }, [validateParams, safeSetState, opts]);

  // 生成支付链接
  const generateLink = useCallback(async (params: GenerateLinkRequest): Promise<GenerateLinkData | null> => {
    cleanup(); // 清理之前的请求
    return executeGenerateLink(params);
  }, [cleanup, executeGenerateLink]);

  // 重试最后一次请求
  const retry = useCallback(async (): Promise<GenerateLinkData | null> => {
    if (!lastParams) {
      const error: ApiError = {
        error_code: -1,
        message: '没有可重试的请求',
        details: null
      };
      
      safeSetState({ error });
      opts.onError(error);
      return null;
    }
    
    return generateLink(lastParams);
  }, [lastParams, generateLink, safeSetState, opts]);

  // 重置状态
  const reset = useCallback(() => {
    cleanup();
    setState({
      data: null,
      loading: false,
      error: null
    });
    setLastParams(null);
  }, [cleanup]);

  // 组件卸载时的清理
  useState(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  });

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    generateLink,
    reset,
    lastParams,
    retry
  };
}

// 辅助函数：创建支付链接请求参数
export function createGenerateLinkParams(
  rechargeId: string,
  paymentType: 'wechat' | 'alipay' | 'voucher' | 'star' | 'usdt',
  channel: 'TMPAY' | 'SJZF' | 'DEFAULT' | 'CLOSED' = 'DEFAULT'
): GenerateLinkRequest {
  return {
    recharge_id: rechargeId,
    type: paymentType,
    channel
  };
}

// 辅助函数：检查支付链接是否过期
export function isPaymentLinkExpired(data: GenerateLinkData): boolean {
  if (!data.expire_time) {
    return false; // 没有过期时间，认为不过期
  }
  
  return Date.now() > data.expire_time * 1000; // expire_time 是秒，需要转换为毫秒
}

// 辅助函数：获取支付链接剩余有效时间（秒）
export function getPaymentLinkRemainingTime(data: GenerateLinkData): number {
  if (!data.expire_time) {
    return Infinity; // 没有过期时间，返回无限大
  }
  
  const remaining = data.expire_time - Math.floor(Date.now() / 1000);
  return Math.max(0, remaining);
}

// 辅助函数：格式化剩余时间为可读字符串
export function formatRemainingTime(seconds: number): string {
  if (seconds === Infinity) {
    return '永不过期';
  }
  
  if (seconds <= 0) {
    return '已过期';
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes > 0) {
    return `${minutes}分${remainingSeconds}秒`;
  }
  
  return `${remainingSeconds}秒`;
}